import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Coupon } from '../../services/api';
import useClickOutside from '../../hooks/useClickOutside';

interface CouponDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  coupon: Coupon | null;
}

const CouponDetailModal: React.FC<CouponDetailModalProps> = ({ isOpen, onClose, coupon }) => {
  // Click outside handler
  const modalRef = useClickOutside(onClose);
  
  if (!isOpen || !coupon) return null;

  const getStatusBadge = (status: string) => {
    const badges = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      expired: 'bg-red-100 text-red-800',
      scheduled: 'bg-blue-100 text-blue-800',
      used_up: 'bg-yellow-100 text-yellow-800'
    };
    return badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-800';
  };

  const DetailItem: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <div>
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className="mt-1 text-sm text-gray-900">{value}</p>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div ref={modalRef} className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Coupon Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <DetailItem label="Code" value={coupon.code} />
            <DetailItem label="Name" value={coupon.name} />
            <DetailItem label="Description" value={coupon.description || 'N/A'} />
            <DetailItem label="Status" value={
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(coupon.computed_status || 'active')}`}>
                {coupon.computed_status?.replace('_', ' ').toUpperCase()}
              </span>
            } />
            <DetailItem label="Discount" value={coupon.computed_discount_display} />
            <DetailItem label="Discount Type" value={coupon.discount_type.replace('_', ' ')} />
            <DetailItem label="Minimum Purchase" value={coupon.minimum_amount ? `${coupon.minimum_amount} Ks` : 'None'} />
            {coupon.discount_type === 'percentage' && (
              <DetailItem label="Maximum Discount" value={coupon.maximum_discount ? `${coupon.maximum_discount} Ks` : 'None'} />
            )}
            <DetailItem label="Usage" value={`${coupon.used_count} / ${coupon.usage_limit || 'Unlimited'}`} />
            <DetailItem label="Start Date" value={coupon.starts_at ? new Date(coupon.starts_at).toLocaleString() : 'N/A'} />
            <DetailItem label="Expires Date" value={coupon.expires_at ? new Date(coupon.expires_at).toLocaleString() : 'Never'} />
            <DetailItem label="Created At" value={new Date(coupon.created_at).toLocaleString()} />
            <DetailItem label="Last Updated" value={new Date(coupon.updated_at).toLocaleString()} />
          </div>
        </div>
        <div className="flex justify-end p-6 border-t">
          <button 
            onClick={onClose} 
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CouponDetailModal;
